import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { SessionCtl } from '../services/session.ctl.service';

/**
 * Matches Klab API URLs routed through the gateway:
 *   /ms-iam/...
 *   /ms-people/...
 *   /ms-organization/...
 *   /ms-report/...
 *   /ms-messaging-ws/...
 *   /ms-notifications/...
 *   /ms-accounting/...
 *   /ms-requirement/...
 *   /ms-procurement/...
 *   /ms-treasury/...
 *   /ms-integration/...
 *   /ms-audit/...
 * Works on absolute URLs (gateway domain) and relative URLs (dev proxy).
 */
const KLAB_API_PATH_REGEX =
    /\/ms-(iam|people|organization|master-data|employee|hr|agreement|payroll|report|messaging-ws|notifications?|accounting|requirement|procurement|treasury|integration|audit)\//;

/**
 * Injects `Authorization: Bearer <access_token>` on every outgoing request to a
 * Klab microservice when there is an active session. Skips if:
 *  - There is no token (login, recovery, etc).
 *  - The request already carries an Authorization header.
 *  - The URL is not a Klab API URL (CDN assets, third-party calls, etc).
 *
 * Required so that endpoints which validate the JWT internally (e.g.
 * POST /api/v1/auth/validate in IAM) can read the bearer. Most other endpoints
 * are permitAll at Spring Security level; the gateway handles their auth.
 */
export const authBearerInterceptor: HttpInterceptorFn = (req, next) => {
    const sessionCtl = inject(SessionCtl);
    const token = sessionCtl.accessToken;

    if (!token) return next(req);
    if (req.headers.has('Authorization')) return next(req);
    if (!KLAB_API_PATH_REGEX.test(req.url)) return next(req);

    return next(
        req.clone({
            setHeaders: { Authorization: `Bearer ${token}` },
        }),
    );
};
